import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgeCountsSchema } from '@web/shared';
import { apiRequest } from '@/lib/api';

/**
 * Unread counts for the bottom-nav tab badges. One small poll for the whole
 * signed-in shell; individual pages call `useClearBadgesOnOpen()` when they
 * clear something server-side (open a thread / room) so the badge updates at
 * once instead of on the next poll.
 */
export function useBadges() {
  return useQuery({
    queryKey: ['badges'],
    refetchInterval: 20_000,
    refetchIntervalInBackground: false,
    queryFn: ({ signal }) => apiRequest('/api/badges', { schema: BadgeCountsSchema, signal }),
  });
}

/** Re-fetch the tab badges now (e.g. after marking notifications read). */
export function useInvalidateBadges() {
  const qc = useQueryClient();
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['badges'] });
  }, [qc]);
}

/**
 * Opening a Whisper thread or a Circle room clears its unread state on the
 * server as a side effect of the GET. Call this with a key that changes per
 * thread/room so the badge count refreshes right after.
 */
export function useClearBadgesOnOpen(key: string | undefined, ready: boolean) {
  const invalidate = useInvalidateBadges();
  useEffect(() => {
    if (key && ready) invalidate();
  }, [key, ready, invalidate]);
}
